import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Prop, State } from '@stencil/core';
import { transformTagNames } from '../../global/transform-tag-name';
import { getDataOnFromTheme, WorkbenchTheme } from '../../shared/themes';
import { ButtonBrandVariant, ButtonSize, ButtonType, ButtonVariant } from './button.types';
import { Keys } from '../../shared/keys';
import { BUTTON_VARIANT_BRAND, DEFAULT_BUTTON_SIZE, DEFAULT_BUTTON_TYPE } from './button.constants';

/**
 * Buttons display different options for actions for the user. They have a clear hierarchy. As a general rule, all
 * Buttons in the form of squares have rounded edges.
 *
 * @slot - Text content placed within button
 * @slot icon - Optional icon
 * @part button - The button or anchor element rendered within the wb-button
 * @slot trailing-icon - Optional trailing icon
 */
@Component({
  tag: 'wb-button',
  styleUrl: 'scss/button.scss',
  shadow: true,
})
export class Button implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /** Displays loading state */
  @Prop({ reflect: true })
  public loading: boolean = false;

  /** Button variant */
  @Prop({ reflect: true })
  public variant!: ButtonVariant;

  /** Brand name for the brand variant */
  @Prop({ reflect: true })
  public brand: ButtonBrandVariant;

  /** Button size */
  @Prop({ reflect: true })
  public size: ButtonSize = DEFAULT_BUTTON_SIZE;

  /**
   * Colour scheme
   * @deprecated Use `data-on` attribute instead
   * */
  @Prop({ reflect: true })
  public theme: WorkbenchTheme;

  /** Hides text and shows only icon */
  @Prop({ reflect: true })
  public iconOnly: boolean = false;

  /** Shows the button as disabled and prevents interaction */
  @Prop({ reflect: true })
  public disabled: boolean = false;

  /** Allow focus when it's disabled */
  @Prop({ reflect: true })
  public focusableWhenDisabled: boolean = false;

  /** Type of the button */
  @Prop()
  public type: ButtonType = DEFAULT_BUTTON_TYPE;

  /** Renders an anchor tag linked to the passed in URL (fragment) */
  @Prop()
  public href: string | undefined;

  /** Can be used in combination with `href` property */
  @Prop()
  public download: string | undefined;

  /** Can be used in combination with `href` property */
  @Prop()
  public rel: string | undefined;

  /** Can be used in combination with `href` property */
  @Prop()
  public target: string | undefined;

  /** Emitted when the button receives focus */
  @Event()
  public wbfocus!: EventEmitter<void>;

  /** Emitted when the button loses focus */
  @Event()
  public wbblur!: EventEmitter<void>;

  private TagNames = transformTagNames(this, 'wb-button', ['wb-spinner', 'wb-brand-logo']);

  private handleClick = (event: Event | KeyboardEvent) => {
    if (this.disabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    const shouldTriggerSubmitOnForm = !this.href && this.type === 'submit';
    if (shouldTriggerSubmitOnForm) {
      // this button wants to specifically submit a form
      // climb up the dom to see if we're in a <form>
      // and if so, then use JS to submit it
      const form = this.el.closest('form');

      if (form) {
        event.preventDefault();

        const fakeButton = document.createElement('button');
        fakeButton.type = 'submit';
        fakeButton.style.display = 'none';
        form.appendChild(fakeButton);
        fakeButton.click();
        fakeButton.remove();
      }
    }
  };

  @State()
  private isActive: boolean = false;

  private buttonEl: HTMLElement;

  private bindRef = (el: HTMLElement) => {
    this.buttonEl = el;
  };

  private handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === Keys.SPACE) {
      event.preventDefault();
      this.isActive = true;
    }
  };

  private handleKeyUp = (event: KeyboardEvent) => {
    if (event.key === Keys.SPACE) {
      this.isActive = false;
      this.buttonEl.click();
    }
  };

  private onFocus = () => this.wbfocus.emit();

  private onBlur = () => this.wbblur.emit();

  public render(): JSX.Element {
    const { WbSpinner, WbBrandLogo } = this.TagNames;

    const content = this.variant === BUTTON_VARIANT_BRAND ? (
      <WbBrandLogo name={this.brand} size="m" />
    ) : (
      [
        <slot name="icon" />,
        <slot />,
        <slot name="trailing-icon" />,
      ]
    );

    const shouldDisabledBeFocusable = this.disabled && this.focusableWhenDisabled;
    const shouldDisabledNotBeFocusable = this.disabled && !this.focusableWhenDisabled;

    return (
      <Host //
        class="wb-button"
        onKeyDown={this.handleKeyDown}
        onKeyUp={this.handleKeyUp}
        data-on={getDataOnFromTheme(this.el, this.theme)}
        loading={this.loading ? '' : null}
      >
        {this.href && !this.disabled /* render button if component is disabled, which prevents click listeners from firing */ ? (
          <a //
            ref={this.bindRef}
            class={{ button: true, active: this.isActive }}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            onClick={this.handleClick}
            aria-label={this.el.getAttribute('aria-label')}
            href={this.href}
            rel={this.rel}
            target={this.target}
            download={this.download}
            part="button"
          >
            {content}
          </a>
        ) : (
          <button //
            ref={this.bindRef}
            class={{ button: true, active: this.isActive }}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            onClick={this.handleClick}
            type={this.type}
            disabled={shouldDisabledNotBeFocusable || this.loading}
            aria-disabled={shouldDisabledBeFocusable ? 'true' : 'false'}
            tabindex={shouldDisabledBeFocusable ? '0' : undefined}
            aria-label={this.el.getAttribute('aria-label')}
            part="button"
          >
            {content}
          </button>
        )}
        {this.loading && <WbSpinner size={this.size} data-on-brand={this.variant === 'primary'} />}
      </Host>
    );
  }
}
